import { Component, OnInit } from "@angular/core";
import { NgxSpinnerService } from "ngx-spinner";
import { ConsignmentService, GalleryConsignmentProduct } from "src/app/Services/consignment.service";
import { ArtistProductsService, ArtistProductSummary, ArtistProductsSummaryRequest } from "src/app/Services/artistproducts.service";
import { AuthService, AuthConfig } from "src/app/Services/auth.service";
import { NotificationsService } from "src/app/Services/shared/notifications.service";
import { NotificationTypes } from "src/app/app.enums";
import { ImageUtils } from "src/app/shared/utils/image-utils";
import { TranslocoService } from "@jsverse/transloco";

@Component({
    selector: "labels-generation",
    templateUrl: "./labels-generation.component.html",
    styleUrls: ["./labels-generation.component.css"],
    standalone: false
})
export class LabelsGenerationComponent implements OnInit {
  // List of available products for selection
  public galleryProducts: (GalleryConsignmentProduct | ArtistProductSummary)[] = [];
  // List of selected products for info card generation
  public selectedProducts: (GalleryConsignmentProduct | ArtistProductSummary)[] = [];
  // IDs of selected products
  public selectedProductIds: string[] = [];
  // Flag to control product details dialog visibility
  public showProductDialog = false;
  // ID of the product to display in the dialog
  public productId: string | null = null;
  // Flag to control skeleton loader visibility
  public isLoading: boolean = true;
  // Auth configuration to determine user role
  public authConfig: AuthConfig = {};
  // Artist ID
  public artistId: string | null = null;
  // Constructor with dependency injection
  constructor(
    private ngxSpinnerService: NgxSpinnerService,
    private consignmentService: ConsignmentService,
    private artistProductsService: ArtistProductsService,
    private authService: AuthService,
    private notificationsService: NotificationsService,
    private transloco: TranslocoService
  ) { }

  // Initialize component by loading products based on user role
  ngOnInit(): void {
    this.authConfig = this.authService.getAuthConfig();
    if (this.authConfig.IsArtist) {
      this.artistId = this.authService.getUserId();
      this.getArtistProducts();
    } else if (this.authConfig.IsGallery) {
      this.getGalleryProducts();
    } else {
      this.notificationsService.showNotification(
        this.transloco.translate('labelsGeneration.error'),
        this.transloco.translate('labelsGeneration.featureArtistsGalleriesOnly'),
        NotificationTypes.Error
      );
    }
  }

  // Helper method to check if product has 'Product' property (GalleryConsignmentProduct)
  public hasProductProperty(product: GalleryConsignmentProduct | ArtistProductSummary): product is GalleryConsignmentProduct {
    return (product as GalleryConsignmentProduct).Product !== undefined;
  }

  // Helper method to get product name
  public getProductName(product: GalleryConsignmentProduct | ArtistProductSummary): string {
    return this.hasProductProperty(product) ? product.Product.Name : product.Name;
  }

  // Helper method to get product ID
  public getProductId(product: GalleryConsignmentProduct | ArtistProductSummary): string {
    return this.hasProductProperty(product) ? product.Product.Id : product.Id;
  }

  // Helper method to get product image URL
  public getProductImageUrl(product: GalleryConsignmentProduct | ArtistProductSummary): string {
    if (this.hasProductProperty(product)) {
      return product.Product.ResizedImageUrl || product.Product.ProductImgUrl || 'assets/images/No-Image.png';
    } else {
      return product.ResizedImageUrl || product.ProductImgUrl || 'assets/images/No-Image.png';
    }
  }

  // Fetch gallery products for info card generation
  public getGalleryProducts(): void {
    this.isLoading = true;
    this.ngxSpinnerService.show();
    this.consignmentService.getGalleryConsignmentProducts(null, {}).subscribe({
      next: (galleryProducts: GalleryConsignmentProduct[]) => {
        this.galleryProducts = galleryProducts
          .filter(product => product.IsProductAcceptedByGallery && !product.IsProductSold)
          .map(product => {
            product.Product.ResizedImageUrl = ImageUtils.getResizedCloudinaryUrl(
              product.Product.ProductImgUrl || 'assets/images/No-Image.png'
            );
            return product;
          });
        this.isLoading = false;
        this.ngxSpinnerService.hide();
      },
      error: (error: any) => {
        console.error('Error loading gallery products:', error);
        this.isLoading = false;
        this.ngxSpinnerService.hide();
        this.notificationsService.showNotification(
          this.transloco.translate('labelsGeneration.error'),
          this.transloco.translate('labelsGeneration.failedToLoadProducts'),
          NotificationTypes.Error
        );
      }
    });
  }

public getArtistProducts(): void {
  if (!this.artistId) {
    this.notificationsService.showNotification(
      this.transloco.translate('labelsGeneration.error'),
      this.transloco.translate('labelsGeneration.artistIdNotFound'),
      NotificationTypes.Error
    );
    return;
  }

  this.isLoading = true;
  this.ngxSpinnerService.show();
  const filters: ArtistProductsSummaryRequest = {
    status: 'Available'
  };

  this.artistProductsService.getArtistProductsSummaryResponse(this.artistId, filters).subscribe({
    next: (artistProducts: ArtistProductSummary[]) => {
      this.galleryProducts = artistProducts
        .filter(p => !p.IsSold && !p.IsConsignedProduct) // Ensure only available products
        .map(product => {
          product.ResizedImageUrl = ImageUtils.getResizedCloudinaryUrl(
            product.ProductImgUrl || 'assets/images/No-Image.png'
          );
          return product;
        });
      this.isLoading = false;
      this.ngxSpinnerService.hide();
    },
    error: (error: any) => {
      console.error('Error loading artist products:', error);
      this.isLoading = false;
      this.ngxSpinnerService.hide();
      this.notificationsService.showNotification(
        this.transloco.translate('labelsGeneration.error'),
        this.transloco.translate('labelsGeneration.failedToLoadProducts'),
        NotificationTypes.Error
      );
    }
  });
}

  // Select a product for info card generation
  public selectProduct(product: GalleryConsignmentProduct | ArtistProductSummary): void {
    const productId = this.getProductId(product);
    const isAlreadySelected = this.selectedProducts.some(p => this.getProductId(p) === productId);

    if (!isAlreadySelected) {
      this.selectedProducts.push(product);
      this.selectedProductIds.push(productId);
      const index = this.galleryProducts.findIndex(p => this.getProductId(p) === productId);
      if (index !== -1) {
        this.galleryProducts.splice(index, 1);
      }
      console.log('Product selected:', this.getProductName(product));
      console.log('Selected product IDs:', this.selectedProductIds);
    }
  }

  // Remove a product from selection
  public removeFromSelection(product: GalleryConsignmentProduct | ArtistProductSummary): void {
    const productId = this.getProductId(product);
    const selectedIndex = this.selectedProducts.findIndex(p => this.getProductId(p) === productId);
    if (selectedIndex !== -1) {
      this.selectedProducts.splice(selectedIndex, 1);
    }
    const idIndex = this.selectedProductIds.findIndex(id => id === productId);
    if (idIndex !== -1) {
      this.selectedProductIds.splice(idIndex, 1);
    }
    this.galleryProducts.push(product);
    console.log('Product removed from selection:', this.getProductName(product));
    console.log('Selected product IDs:', this.selectedProductIds);
  }

  // Generate info cards by calling API to download PDF
  public onGenerateInfoCards(): void {
    if (this.selectedProductIds.length === 0) {
      this.notificationsService.showNotification(
        this.transloco.translate('labelsGeneration.warning'),
        this.transloco.translate('labelsGeneration.selectProductForInfoCards'),
        NotificationTypes.Error
      );
      return;
    }

    console.log('Generating info cards for product IDs:', this.selectedProductIds);
    this.ngxSpinnerService.show();

    this.consignmentService.generateInfoCardsPdf(this.selectedProductIds).subscribe({
      next: (response: Blob) => {
        this.ngxSpinnerService.hide();
        if (response) {
          this.downloadPdf(response, 'product-info-cards.pdf');
          this.notificationsService.showNotification(
            this.transloco.translate('labelsGeneration.success'),
            this.transloco.translate('labelsGeneration.infoCardsGenerated'),
            NotificationTypes.Success
          );
        }
      },
      error: (error: any) => {
        this.ngxSpinnerService.hide();
        console.error('Error generating info cards:', error);
        this.notificationsService.showNotification(
          this.transloco.translate('labelsGeneration.error'),
          this.transloco.translate('labelsGeneration.failedToGenerateInfoCards'),
          NotificationTypes.Error
        );
      }
    });
  }

  // Generate stickers by calling API to download PDF
  public onGenerateStickers(): void {
    if (this.selectedProductIds.length === 0) {
      this.notificationsService.showNotification(
        this.transloco.translate('labelsGeneration.warning'),
        this.transloco.translate('labelsGeneration.selectProductForStickers'),
        NotificationTypes.Error
      );
      return;
    }

    console.log('Generating stickers for product IDs:', this.selectedProductIds);
    this.ngxSpinnerService.show();
    this.consignmentService.generateStickersPdf(this.selectedProductIds).subscribe({
      next: (response: Blob) => {
        this.ngxSpinnerService.hide();
        if (response) {
          this.downloadPdf(response, 'product-stickers.pdf');
          this.notificationsService.showNotification(
            this.transloco.translate('labelsGeneration.success'),
            this.transloco.translate('labelsGeneration.stickersGenerated'),
            NotificationTypes.Success
          );
        }
      },
      error: (error: any) => {
        this.ngxSpinnerService.hide();
        console.error('Error generating stickers:', error);
        this.notificationsService.showNotification(
          this.transloco.translate('labelsGeneration.error'),
          this.transloco.translate('labelsGeneration.failedToGenerateStickers'),
          NotificationTypes.Error
        );
      }
    });
  }

  // Download PDF file from API response
  private downloadPdf(pdfData: Blob, filename: string): void {
    const url = window.URL.createObjectURL(pdfData);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  // Open product details dialog with specified product ID
  public openProductDialog(productId: string): void {
    this.productId = productId;
    this.showProductDialog = true;
  }

  // Close product details dialog and reset product ID
  public closeProductDialog(): void {
    this.showProductDialog = false;
    this.productId = null;
  }
}